import React                      from 'react';
import View                       from '@widesk-ui/components/View';
import Segmented                  from '@widesk-ui/Segmented';
import Field                      from '@/TokamapEditor/ux/AreaSidebar/Field';
import HelpTip                    from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import type { OrientationMode }   from '@/TokamapEditor/ux/AreaSidebar/orientation';
import type { OrientationValues } from '@/TokamapEditor/ux/AreaSidebar/orientation';
import type { ThemeLockRef }      from '@/TokamapEditor/helpers/themeImposed';

/**
 * Réunit « suivre la carte », « angle fixe » et « toujours à l'endroit » en un seul réglage, plus
 * « lisible » pour un texte (`readable`).
 */
export default function OrientationField({ mode, mixed, effectiveRotation, onChange, slider, lockedBy, readable }: {
	mode?: OrientationMode;
	mixed?: boolean;
	/** Angle affiché à l'instant (celui de la carte tant qu'aucun n'est choisi) : point de départ d'« Angle fixe ». */
	effectiveRotation: number;
	onChange: (values: OrientationValues) => void;
	/** Curseur d'angle, montré seulement en « Angle fixe ». */
	slider: React.ReactNode;
	lockedBy?: ThemeLockRef;
	readable?: boolean;
}) {
	const notReadable = readable ? { keepReadable: undefined } : {};

	return (
		<Field
			lockedBy={lockedBy}
			label={(
				<>
					Orientation
					<HelpTip>
						Suit la carte : au sol, orienté comme la vue de départ, pour se lire à l'endroit en
						arrivant. Angle fixe : au sol, à l'angle choisi.
						{readable && ' Lisible : au sol, à l\'angle choisi, mais retourné quand la carte pivote assez pour le montrer à l\'envers.'}
						{' '}Toujours droit : à l'endroit à l'écran, même quand la carte pivote.
					</HelpTip>
				</>
			)}
		>
			<View gap="sm">
				<Segmented
					block
					size="small"
					value={mixed ? undefined : mode}
					onChange={value => {
						if (value === 'map') onChange({ rotation: undefined, rotateWithMap: undefined, ...notReadable });
						if (value === 'fixed') onChange({ rotation: effectiveRotation, rotateWithMap: undefined, ...notReadable });
						if (value === 'readable') onChange({ rotation: effectiveRotation, rotateWithMap: undefined, keepReadable: true });
						if (value === 'screen') onChange({ rotation: undefined, rotateWithMap: true, ...notReadable });
					}}
					options={[
						{ value: 'map', label: 'Suit la carte' },
						{ value: 'fixed', label: 'Angle fixe' },
						...(readable ? [{ value: 'readable', label: 'Lisible' }] : []),
						{ value: 'screen', label: 'Toujours droit' },
					]}
				/>

				{(mode === 'fixed' || mode === 'readable') && !mixed && slider}
			</View>
		</Field>
	);
}
